// Single source of truth for the case-studies redesign (June 2026).
// Plain data only (no JSX) so it can also be imported by vite.config.ts (sitemap)
// and the structured-data / detail pages when those are wired up.

export type IndustryId = "cpg" | "dtc" | "dental";

export interface Industry {
  id: IndustryId;
  /** Full label shown on the card pill, e.g. "Consumer Goods (CPG)". */
  label: string;
  /** Short label shown on the rail, e.g. "Consumer Goods". */
  short: string;
}

export interface CaseStudyMetric {
  value: string;
  caption: string;
}

export interface CaseStudy {
  slug: string;
  industryId: IndustryId;
  readTime: string;
  headline: string;
  summary: string;
  metric: CaseStudyMetric;
  tags: string[];
  /** Optional source repo. Not linked yet — detail pages are deferred. */
  githubUrl?: string;
}

// Order here drives the rail order and the pinned-scroll order.
export const INDUSTRIES: Industry[] = [
  { id: "cpg", label: "Consumer Goods (CPG)", short: "Consumer Goods" },
  { id: "dtc", label: "DTC E-commerce", short: "DTC E-commerce" },
  { id: "dental", label: "Dental", short: "Dental" },
];

export const CASE_STUDIES: CaseStudy[] = [
  // ---- Consumer Goods (CPG) ----
  {
    slug: "netsuite-edi-multi-3pl-order-to-cash",
    industryId: "cpg",
    readTime: "6 min read",
    headline:
      "Wholesale Order-to-Cash at 95% Less Manual Entry With Automated EDI and Multi-3PL Routing",
    summary:
      "One idempotent pipeline that turns retail purchase orders into shipped, invoiced, reconciled revenue across multiple 3PLs, with humans touching only the exceptions.",
    metric: { value: "95%", caption: "Less manual order entry across channels" },
    tags: ["EDI Integration", "NetSuite", "Order-to-Cash", "Temporal"],
  },
  {
    slug: "retail-deduction-chargeback-recovery",
    industryId: "cpg",
    readTime: "6 min read",
    headline:
      "Recovering Retail Deductions at 70% Win Rate With LLM Document Intelligence",
    summary:
      "An agent that reads every cryptic retailer deduction, checks it against your order-to-cash record, and assembles the dispute, so the invalid ones stop being silently written off.",
    metric: { value: "70%", caption: "Win rate on disputed deductions" },
    tags: ["Document Intelligence", "Haiku 4.5", "Opus 4.8", "Deductions"],
  },
  {
    slug: "retailer-compliance-rules-engine",
    industryId: "cpg",
    readTime: "6 min read",
    headline:
      "Turning 100-Page Retailer Compliance Guides Into Rules That Prevent Chargebacks",
    summary:
      "An LLM reads each retailer's vendor manual into machine-enforceable rules that drive your order-to-cash pipeline, so violations get caught before they ship, not billed back after.",
    metric: { value: "80%", caption: "Fewer compliance chargebacks" },
    tags: ["Document Intelligence", "Opus 4.8", "RAG", "Compliance"],
  },
  {
    slug: "otif-chargeback-prevention",
    industryId: "cpg",
    readTime: "6 min read",
    headline:
      "Stopping Retail Chargebacks Before They Hit With Per-PO OTIF Risk Prediction",
    summary:
      "A real-time layer over the existing order flow that scores every purchase order for on-time-in-full risk and alerts the team to intervene before the delivery window closes and the fine triggers.",
    metric: { value: "-85%", caption: "Compliance and OTIF chargebacks" },
    tags: ["OTIF", "Chargeback Prevention", "Temporal", "Retail"],
  },

  // ---- DTC E-commerce ----
  {
    slug: "contribution-margin-data-engineering",
    industryId: "dtc",
    readTime: "6 min read",
    headline:
      "Profitable Growth Decisions With True Contribution Margin by Channel, Refreshed Daily",
    summary:
      "A warehouse-native cost model that loads every order with its real variable costs, so CAC, payback, and contribution margin are finally true by channel, campaign, and cohort.",
    metric: {
      value: "+6 pts",
      caption: "Blended contribution margin after reallocating spend",
    },
    tags: ["Contribution Margin", "BigQuery", "dbt", "Marketing Data"],
  },
  {
    slug: "multi-touch-attribution-revenue-feedback",
    industryId: "dtc",
    readTime: "7 min read",
    headline:
      "Lower CAC at Scale With First-Party Multi-Touch Attribution and Revenue Feedback to the Ad Platforms",
    summary:
      "First-party, server-side tracking and data-driven attribution that reveal which channels truly drive new revenue, then feed that value back to Google and Meta so the algorithms optimize on reality, not last click.",
    metric: {
      value: "-22%",
      caption: "Blended CAC after reallocating to truly incremental channels",
    },
    tags: [
      "Multi-Touch Attribution",
      "Rust",
      "ClickHouse",
      "Meta CAPI",
      "Google Ads",
    ],
  },
  {
    slug: "creative-intelligence",
    industryId: "dtc",
    readTime: "6 min read",
    headline:
      "Learning Which Creative Attributes Win With a Multimodal Creative-Intelligence Layer",
    summary:
      "A vision LLM tags every ad creative into structured attributes and joins them to your attribution and margin data, so you see which creative traits drive the cheapest, highest-margin new customers, not which ad id got clicks.",
    metric: { value: "2x", caption: "Win rate on new creative concepts" },
    tags: ["Creative Intelligence", "Sonnet 4.6 Vision", "BigQuery", "Growth"],
  },
  {
    slug: "landed-cost-extraction",
    industryId: "dtc",
    readTime: "6 min read",
    headline:
      "Keeping Contribution Margin Honest by Extracting Landed Cost From Messy Documents",
    summary:
      "An LLM reads supplier invoices, freight and customs bills, 3PL fee schedules, and processor statements into the cost model, so contribution margin reflects true landed cost without manual data entry, even as tariffs and freight move.",
    metric: { value: "Days, not quarters", caption: "To update true landed cost" },
    tags: ["Document Intelligence", "Haiku 4.5", "Opus 4.8", "Landed Cost"],
  },
  {
    slug: "true-net-profit-pnl",
    industryId: "dtc",
    readTime: "5 min read",
    headline:
      "True Net Profit in One Number, Pulled From Every Sales and Cost Source",
    summary:
      "A reconciled, automated P&L for a small brand: sales, every variable cost, and overhead from the books, combined into the real net profit the founder can finally trust, refreshed daily.",
    metric: {
      value: "+5 pts",
      caption: "Net margin, after the real number exposed the leaks",
    },
    tags: ["Profit", "dbt", "Metabase", "QuickBooks"],
  },
  {
    slug: "ai-customer-support-agent",
    industryId: "dtc",
    readTime: "7 min read",
    headline:
      "A Support Agent That Resolves Tickets in Your Voice and Never Invents a Policy",
    summary:
      "A governed AI agent that resolves the common tickets end to end using facts pulled from Shopify, takes only the actions your policy allows, escalates everything else, and states zero facts it did not verify in code.",
    metric: { value: "60%", caption: "Of tickets resolved end to end" },
    tags: ["AI Agent", "Governed", "Facts-of-Record", "Shopify"],
  },

  // ---- Dental ----
  {
    slug: "patient-attribution-revenue-feedback",
    industryId: "dental",
    readTime: "6 min read",
    headline:
      "Lower Cost Per Booked Patient by Attributing Ad Spend to Real Treatments, Not Form-Fills",
    summary:
      "First-party attribution that connects each ad click to the appointment booked in the practice system and the treatment that follows, then feeds that real value back to Google and Meta.",
    metric: { value: "-30%", caption: "Cost per acquired patient" },
    tags: ["Multi-Touch Attribution", "Google Ads", "Meta CAPI", "Dental"],
  },
  {
    slug: "llm-intake-recall-automation",
    industryId: "dental",
    readTime: "6 min read",
    headline:
      "Booking More Treatment and Killing the Re-Keying With LLM Patient-Lifecycle Automation",
    summary:
      "An automation layer over Dentally that pulls intake and referral documents straight into the record, and reads clinical notes to surface and personalize the follow-up that actually books treatment, with the front desk approving every step.",
    metric: { value: "+22%", caption: "Treatment plans booked" },
    tags: ["Document Intelligence", "Haiku 4.5", "Opus 4.8", "Dentally"],
  },
  {
    slug: "marketing-command-center-dashboard",
    industryId: "dental",
    readTime: "6 min read",
    headline:
      "Every Marketing Source and Patient Outcome in One Dashboard You Can Just Ask",
    summary:
      "Meta, Google, Search Console, GA4, and Dentally unified into one live custom dashboard, with an LLM that answers plain-English questions tied to booked patients and treatment.",
    metric: { value: "8 hrs/week", caption: "Reclaimed from manual reporting" },
    tags: ["Marketing Dashboard", "BigQuery", "LLM Analytics"],
  },
  {
    slug: "ai-front-desk-assistant",
    industryId: "dental",
    readTime: "7 min read",
    headline:
      "A 24/7 Front Desk That Books Patients and Refuses to Play Doctor",
    summary:
      "A governed AI assistant that answers admin enquiries from approved practice information and books through Dentally, escalates anything clinical with a hard safety gate, and never gives clinical advice, with staff reviewing every non-templated reply.",
    metric: {
      value: "70%",
      caption: "Of enquiries resolved without the front desk (booking, FAQs, hours)",
    },
    tags: ["AI Workflow", "Governed", "Clinical-Safety Gate", "Dentally"],
  },
];

export function studiesByIndustry(id: IndustryId): CaseStudy[] {
  return CASE_STUDIES.filter((study) => study.industryId === id);
}

export function industryById(id: IndustryId): Industry {
  const industry = INDUSTRIES.find((i) => i.id === id);
  if (!industry) throw new Error(`Unknown industry id: ${id}`);
  return industry;
}
